import {
  Component,
  ElementRef,
  HostListener,
  ViewEncapsulation,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HeaderComponent, type HeaderNavItem } from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../core/i18n';
import { openExternalUrlInNewTab } from '../../navigation/app-navigation.config';

/**
 * Application header with branded logo, primary navigation, language toggle, and app-owned user menu.
 */
@Component({
  selector: 'app-shell-header',
  imports: [HeaderComponent, TranslatePipe],
  templateUrl: './shell-header.html',
  styleUrl: './shell-header.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-shell__header' },
})
export class ShellHeader {
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  readonly navItems = input.required<HeaderNavItem[]>();
  readonly languageLabel = input.required<string>();
  readonly languageToggle = output<void>();
  readonly logOut = output<void>();

  private readonly userMenuNav = viewChild<ElementRef<HTMLElement>>('userMenuNav');

  protected readonly userMenuOpen = signal(false);
  private avatarButton: HTMLButtonElement | null = null;

  /**
   * Handles header host clicks for OM-825 external drawer links and the user avatar menu.
   * The mobile drawer ignores HeaderNavItem.onClick and navigates via href; intercept http(s)
   * anchors so destinations open in a new tab without leaving ESO Lite.
   * ui-kit 1.4.0 requires `[isLoggedIn]="true"` for the avatar and no longer emits
   * `userAvatarClick` (it toggles its own empty menu).
   */
  @HostListener('click', ['$event'])
  protected onHostClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    // Mobile drawer ignores HeaderNavItem.onClick; intercept only drawer anchors (OM-825).
    const drawerExternalLink = target.closest(
      'a.puro-header__drawer-item[href^="http"], a.puro-header__drawer-subitem[href^="http"]',
    );
    if (drawerExternalLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      openExternalUrlInNewTab(drawerExternalLink.href);
      return;
    }

    if (!target.closest('.puro-header__avatar-btn')) {
      return;
    }

    this.toggleUserMenu();
  }

  /**
   * Toggles the app-owned user avatar dropdown.
   */
  protected toggleUserMenu(): void {
    if (!this.userMenuOpen()) {
      this.avatarButton = this.hostElement.nativeElement.querySelector(
        '.puro-header__avatar-btn',
      ) as HTMLButtonElement | null;
    }

    this.userMenuOpen.update((open) => !open);

    if (this.userMenuOpen()) {
      queueMicrotask(() => {
        const firstItem = this.userMenuNav()?.nativeElement.querySelector(
          'a, button',
        ) as HTMLElement | null;
        firstItem?.focus();
      });
      return;
    }

    this.avatarButton?.focus();
  }

  /**
   * Closes the user menu when Escape is pressed while it is open.
   */
  @HostListener('document:keydown.escape')
  protected onEscapeKey(): void {
    if (!this.userMenuOpen()) {
      return;
    }

    this.userMenuOpen.set(false);
    this.avatarButton?.focus();
  }

  /**
   * Closes the user menu when focus moves outside the header dropdown.
   */
  @HostListener('document:focusin', ['$event'])
  protected onDocumentFocusIn(event: FocusEvent): void {
    this.closeMenuIfOutside(event.target);
  }

  /**
   * Closes the user menu when a pointer click lands outside the dropdown and the avatar.
   * Complements the focusin handler, which only fires for focusable targets; plain clicks on
   * non-focusable areas (page body, text) must also dismiss the menu (OM-120).
   */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    this.closeMenuIfOutside(event.target);
  }

  /** Closes the user menu when the event target is outside the dropdown and avatar button. */
  private closeMenuIfOutside(target: EventTarget | null): void {
    if (!this.userMenuOpen()) {
      return;
    }

    if (!(target instanceof Element)) {
      return;
    }

    const menu = this.userMenuNav()?.nativeElement;
    if (menu?.contains(target) || target.closest('.puro-header__avatar-btn')) {
      return;
    }

    this.userMenuOpen.set(false);
  }
}
